import { useState } from "react";
import { useDeleteRiderMutation, useFetchRidersQuery } from "../store";
import Pagination from "../components/common/Pagination.tsx";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn.tsx";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";
import TextField from "../components/common/TextField.tsx";
import useDebounce from "../hooks/useDebounce.tsx";
import { Link } from "react-router-dom";
import { Rider, Status, TransportTypeLabels } from "../types";
import { showToast, ToastType } from "../utils/toast.utils.ts";
import Modal from "../components/common/Modal.tsx";
import Button from "../components/common/Button.tsx";
import { centeredTdClasses, tdClasses, thClasses } from "../configs";

const Riders: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [name, setName] = useState<string>("");
  const debouncedName = useDebounce(name, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [riderToDelete, setRiderToDelete] = useState<Rider | null>(null);

  const [deleteRider] = useDeleteRiderMutation();

  const confirmDelete = (riderToDelete: Rider) => {
    setRiderToDelete(riderToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteRider = async () => {
    if (!riderToDelete) return;
    const result = await deleteRider(riderToDelete._id);
    if (!result.error) {
      showToast("Livreur supprimé", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setRiderToDelete(null);
  };

  const { data: riders } = useFetchRidersQuery({
    page,
    perPage,
    name: debouncedName,
  });

  return (
    <div className="w-full flex flex-col mx-auto p-4">
      <div className="flex items-center justify-between">
        <div className="w-1/4">
          <TextField
            id="name"
            value={name}
            onChange={(e) => setName((e.target as HTMLInputElement).value)}
            placeholder="Nom ou prénom du livreur"
          />
        </div>
        <NavigationLinkBtn
          to="/riders/create"
          label="Créer un Livreur"
        />
      </div>
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col className="w-3/12" />
          <col className="w-3/12" />
          <col className="w-3/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
        </colgroup>
        <thead>
          <tr>
            <th
              key={0}
              className={thClasses}
            >
              Nom
            </th>
            <th
              key={1}
              className={thClasses}
            >
              Téléphone
            </th>
            <th
              key={2}
              className={thClasses}
            >
              Véhicule
            </th>
            <th
              key={3}
              className={thClasses}
            >
              Status
            </th>
            <th
              key={4}
              className={thClasses}
            >
              Modifier
            </th>
            <th
              key={5}
              className={thClasses}
            >
              Supprimer
            </th>
          </tr>
        </thead>
        <tbody>
          {riders?.data?.map((row) => {
            return (
              <tr key={row._id}>
                <td className={tdClasses}> {row.fullName} </td>
                <td className={tdClasses}> {row.phone} </td>
                <td className={centeredTdClasses}>
                  {TransportTypeLabels[row.transportType]}
                </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <span
                      className={`inline-block w-3 h-3 rounded-full ${
                        row.status === Status.ONLINE
                          ? "bg-green-300"
                          : "bg-gray-300"
                      }`}
                      title={row.status}
                    />
                  </div>
                </td>
                <td className={tdClasses}>
                  <Link
                    className="w-full flex justify-center"
                    to={row._id}
                  >
                    <FaEdit className="w-8 text-primary" />{" "}
                  </Link>
                </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <FaRegTrashAlt
                      className="w-8 text-primary cursor-pointer"
                      onClick={() => {
                        confirmDelete(row);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: riders?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-300 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-primary text-white px-4 py-2 rounded"
                onClick={handleDeleteRider}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="bg-white text-center font-semibold">
            Êtes-vous sûr de vouloir supprimer {riderToDelete?.fullName} ?
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Riders;
